import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DISCIPLINE_LABEL_KEYS = {
  architectural: 'architectural',
  structural: 'structural',
  electrical: 'electrical',
  mechanical: 'mechanical',
  plumbing: 'plumbing',
  civil: 'civil',
  fire_protection: 'fireProtection',
  landscape: 'landscape',
  other: 'other',
};

const DrawingCard = ({ drawing, onViewVersions, onDownload, canUpload }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb?.toFixed(2)} MB`;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return 'FileText';
    if (mimeType?.includes('image')) return 'FileImage';
    return 'File';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg flex-shrink-0">
            <Icon name={getFileIcon(drawing?.mime_type)} size={20} color="var(--color-accent)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground mb-1 truncate">{drawing?.drawing_name}</h3>
            <p className="text-sm text-muted-foreground truncate">{drawing?.site_name || t('unknownSite')}</p>
          </div>
        </div>
        {(drawing?.drawing_status === 'current' || drawing?.is_latest) && (
          <span className="px-2 py-1 rounded-md text-xs font-medium bg-success/10 text-success flex-shrink-0">
            Current
          </span>
        )}
        {drawing?.drawing_status === 'superseded' && (
          <span className="px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground flex-shrink-0">
            Superseded
          </span>
        )}
      </div>
      {(drawing?.drawing_number || drawing?.revision || drawing?.discipline) && (
        <div className="mb-3 px-3 py-2 bg-muted/50 rounded-md space-y-1">
          {drawing?.drawing_number && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t('drawingNo')}</p>
              <p className="text-xs font-medium text-foreground">{drawing?.drawing_number}</p>
            </div>
          )}
          {drawing?.revision && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t('revision')}</p>
              <p className="text-xs font-medium text-foreground">{drawing?.revision}</p>
            </div>
          )}
          {drawing?.discipline && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t('discipline')}</p>
              <p className="text-xs font-medium text-foreground">{t(DISCIPLINE_LABEL_KEYS?.[drawing?.discipline]) || drawing?.discipline}</p>
            </div>
          )}
        </div>
      )}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('uploaded')}:</span>
          <span className="text-foreground font-medium">{formatDate(drawing?.upload_date)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t('size')}:</span>
          <span className="text-foreground font-medium">{formatFileSize(drawing?.file_size)}</span>
        </div>
      </div>
      {drawing?.description && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{drawing?.description}</p>
      )}
      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          fullWidth
          iconName="Eye"
          onClick={() => navigate(`/drawing-viewer/${drawing?.id}`)}
        >
          {t('openViewer')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          onClick={onDownload}
          title={t('download')}
        />
        {drawing?.drawing_number && (
          <Button
            variant="outline"
            size="sm"
            iconName="History"
            onClick={onViewVersions}
            title={t('viewAllVersions')}
          />
        )}
      </div>
    </div>
  );
};

export default DrawingCard;